// Phase 4 of the telemetry plan (§6): the two things both called a heatmap.
//
//   Map    — where on the corridor attention went. Pan centres plotted as
//            density, plus the segment / milepost / analysis bins read out of
//            the clicked sites' own composite ids. This is the one that tells
//            you something, per D-T5, so it leads.
//   Screen — where inside a control people click, on a 10x10 grid measured
//            RELATIVE to that element, so it survives window sizes and layout
//            changes. Answers "is anyone finding this button".
//
// The map half needs no new capture: map.pan already carries "lat,lng z<zoom>"
// and map.site_click carries the site id.
import React, { useMemo, useState } from 'react';
import { CircleMarker, MapContainer, TileLayer, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { Tile } from './usageUi';

const MAP_HEIGHT = 420;
const FALLBACK_CENTRE = [42.3, -72.6];   // Massachusetts: the current audience

/** Marker radius by share of the busiest bin — area, not radius, tracks the
 *  count, because a radius-linear scale makes a 4x busier spot look 16x busier. */
function radiusFor(n, max) {
  const share = max > 0 ? n / max : 0;
  return 5 + Math.sqrt(share) * 17;
}

function heat(share) {
  // cool -> warm, and deliberately not red-green: the dashboard's severity
  // colours are already red-green and this is a different dimension.
  const hue = 205 - 165 * share;
  return `hsl(${hue}, 85%, ${58 - 12 * share}%)`;
}

const MapHeat = ({ map }) => {
  const points = map?.points || [];
  const max = useMemo(() => Math.max(1, ...points.map((p) => p.n)), [points]);
  const centre = useMemo(() => {
    if (!points.length) return FALLBACK_CENTRE;
    // Weighted by visits, so the view opens on where people actually looked
    // rather than the midpoint of every stray pan.
    const w = points.reduce((a, p) => a + p.n, 0) || 1;
    return [points.reduce((a, p) => a + p.lat * p.n, 0) / w,
      points.reduce((a, p) => a + p.lng * p.n, 0) / w];
  }, [points]);

  if (!points.length) {
    return (
      <p className="usage-empty">
        No map activity in this range. Panning and site clicks are recorded as
        soon as someone moves the Safety Dashboard map.
      </p>
    );
  }

  return (
    <div className="ua-heat-map" style={{ height: MAP_HEIGHT }}>
      <MapContainer center={centre} zoom={9} scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: '0.5rem' }}>
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {points.map((p, i) => (
          <CircleMarker key={`${p.lat},${p.lng},${i}`} center={[p.lat, p.lng]}
            radius={radiusFor(p.n, max)}
            pathOptions={{
              color: heat(p.n / max), fillColor: heat(p.n / max),
              fillOpacity: 0.45, weight: 1,
            }}>
            <LTooltip>
              {p.n} view{p.n === 1 ? '' : 's'} · {p.users} user{p.users === 1 ? '' : 's'}
              <br />{p.lat.toFixed(3)}, {p.lng.toFixed(3)}
            </LTooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
};

const Bars = ({ rows, labelOf, max }) => (
  <div className="ua-heat-bars">
    {rows.map((r, i) => (
      <div key={i} className="ua-heat-bar-row">
        <span className="ua-heat-bar-label" title={labelOf(r)}>{labelOf(r)}</span>
        <span className="ua-heat-bar-track">
          <span className="ua-heat-bar-fill"
            style={{ width: `${(r.n / max) * 100}%`, background: heat(r.n / max) }} />
        </span>
        <span className="ua-heat-bar-n">{r.n}</span>
      </div>
    ))}
  </div>
);

/** One control's 10x10 grid. Rendered as a grid of cells rather than a blurred
 *  gaussian: with tens of clicks a blur invents gradients that the data does
 *  not support. */
const ScreenGrid = ({ t }) => (
  <div className="ua-screen-card">
    <div className="ua-screen-head">
      <strong>{t.label || t.target}</strong>
      <span>{t.clicks} click{t.clicks === 1 ? '' : 's'}</span>
    </div>
    <div className="ua-screen-grid" role="img"
      aria-label={`Click distribution within ${t.label || t.target}`}>
      {t.grid.map((n, i) => (
        <span key={i} className="ua-screen-cell"
          title={n ? `${n} click${n === 1 ? '' : 's'} · row ${Math.floor(i / 10) + 1}, col ${(i % 10) + 1}` : ''}
          style={{
            background: n ? heat(n / Math.max(1, t.max)) : 'rgba(148,163,184,0.07)',
            opacity: n ? 0.25 + 0.75 * (n / Math.max(1, t.max)) : 1,
          }} />
      ))}
    </div>
  </div>
);

const TelemetryHeatmaps = ({ data }) => {
  const [dim, setDim] = useState('mileposts');
  const map = data?.map;
  const screen = data?.screen;
  if (!map && !screen) return <p className="usage-empty">No data yet.</p>;

  const dims = {
    mileposts: {
      label: 'milepost',
      rows: map?.mileposts || [],
      labelOf: (r) => `${r.route}  ${r.mp_from.toFixed(1)}–${r.mp_to.toFixed(1)} mi`,
    },
    segments: { label: 'segment', rows: map?.segments || [], labelOf: (r) => r.key },
    analyses: { label: 'analysis', rows: map?.analyses || [], labelOf: (r) => r.key },
    zooms: {
      label: 'zoom level',
      rows: map?.zooms || [],
      labelOf: (r) => `zoom ${r.zoom}`,
    },
  };
  const active = dims[dim];
  const max = Math.max(1, ...active.rows.map((r) => r.n));

  return (
    <>
      <p className="usage-hint">
        Where attention actually went. The map plots the centre of every pan, so
        the hot areas are the stretches people kept coming back to; the
        breakdowns come from the clicked sites&apos; own ids, which carry the
        segment, direction, analysis and milepost.
      </p>

      <div className="usage-tiles">
        <Tile label="Map moves" value={map?.pans ?? '—'} />
        <Tile label="Site clicks" value={map?.site_clicks ?? '—'} />
        <Tile label="Places looked at" value={map?.points?.length ?? '—'}
          hint="distinct pan centres, ~100 m apart" />
        <Tile label="In-control clicks" value={screen?.with_cells ?? '—'}
          hint="clicks that recorded a position" />
      </div>

      <h3>On the corridor</h3>
      <MapHeat map={map} />

      <h3>
        Busiest by
        <span className="ua-table-tools">
          {Object.entries(dims).map(([key, d]) => (
            <button key={key} type="button"
              className={`ua-topn${dim === key ? ' on' : ''}`}
              onClick={() => setDim(key)}>{d.label}</button>
          ))}
        </span>
      </h3>
      {active.rows.length
        ? <Bars rows={active.rows} labelOf={active.labelOf} max={max} />
        : (
          <p className="usage-empty">
            Nothing by {active.label} yet — these come from site clicks on the map.
          </p>
        )}

      <h3>Inside each control</h3>
      <p className="usage-hint">
        A 10×10 grid measured relative to the control itself, so it holds across
        window sizes. Keyboard-activated clicks record no position and are
        counted but not plotted — a control driven by the keyboard shows clicks
        with an empty grid rather than a false corner hotspot.
      </p>
      {screen?.targets?.length ? (
        <div className="ua-screen-wrap">
          {screen.targets.map((t) => <ScreenGrid key={t.target} t={t} />)}
        </div>
      ) : (
        <p className="usage-empty">
          No in-control click positions recorded yet. Capture for this started
          with the phase-4 build, so only sessions after it has rows.
        </p>
      )}
    </>
  );
};

export default TelemetryHeatmaps;
